import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { uploadDocumentFile } from "../services/documents";
import { extractKycWithAiBroker } from "../services/aiBroker";
import { app, socketApp } from "../services";
import ButtonDocsInput from "./ButtonDocsInput";
import SubirDocumentosInteligente from "./SubirDocumentosInteligente";
import {
  formatPhoneMx,
  isValidEmail,
  isValidPhoneMx,
  normalizeEmail,
  onlyDigits,
} from "../utils/fieldValidation";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3030";

const steps = [
  "Datos del solicitante",
  "Documentos (KYC)",
  "Extracción y validación",
  "Crear cuenta y finalizar",
];

const docTypeLabels = {
  acta_nacimiento: "Acta de nacimiento",
  ine: "INE / Identificación oficial",
  curp: "CURP (PDF o imagen)",
  comprobante_domicilio: "Comprobante de domicilio",
  estado_cuenta: "Estados de cuenta",
  rfc: "Constancia de situación fiscal (RFC)",
  otros: "Otros",
};

const productLabelMap = {
  hipotecario: "Credito Hipotecario",
  automotriz: "Credito Automotriz",
  personal: "Credito Personal",
  pyme: "Credito PyME",
};

const isDocumentFieldType = (type) => {
  const value = `${type || ""}`.toLowerCase();
  return (
    value.includes("doc") ||
    value.includes("pdf") ||
    value.includes("imagen") ||
    value.includes("archivo") ||
    value.includes("file")
  );
};

function uid() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function acceptFor(type) {
  switch (type) {
    case "estado_cuenta":
      return "image/*,.pdf,application/pdf,application/octet-stream";
    default:
      return "image/*,.pdf,application/pdf,application/octet-stream";
  }
}

function humanFileSize(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  let b = bytes;
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i++;
  }
  return `${b.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function isValidCurp(curp) {
  if (!curp) return true;
  return /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM][A-Z]{5}[A-Z0-9]\d$/.test(
    String(curp).trim().toUpperCase(),
  );
}

function normalizeDateForInput(value) {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const raw = `${value}`.trim();
  if (!raw) return "";

  const ddmmyyyy = raw.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}-${dd}`;
  }

  const yyyymmdd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (yyyymmdd) return `${yyyymmdd[1]}-${yyyymmdd[2]}-${yyyymmdd[3]}`;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return "";
}

function normalizePhoneForPayload(value) {
  return onlyDigits(value || "").slice(0, 10);
}

function buildClientInviteToken(clientId, institutionId) {
  if (!clientId || !institutionId) return "";
  const payload = `${clientId}:${institutionId}:${Date.now()}`;
  try {
    return window
      .btoa(payload)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  } catch (error) {
    return "";
  }
}

function normalizeMxPhoneForWhatsApp(phone) {
  const digits = onlyDigits(phone || "");
  if (!digits) return "";
  if (digits.length === 10) return `52${digits}`;
  if (digits.length === 12 && digits.startsWith("52")) return digits;
  if (digits.length === 13 && digits.startsWith("521")) {
    return `52${digits.slice(3)}`;
  }
  return digits;
}

function buildWhatsAppInviteUrl({ phone, clientId, institutionId }) {
  const phoneDigits = normalizeMxPhoneForWhatsApp(phone);
  if (!phoneDigits || !clientId || !institutionId) return "";
  const token = buildClientInviteToken(clientId, institutionId);
  const baseUrl = "https://app.lukai.mx";
  const registerUrl = `${baseUrl}?mode=register&id_client=${encodeURIComponent(clientId)}&token=${encodeURIComponent(token)}`;
  const message = [
    "Hola, te damos la bienvenida a Lukai.",
    "Lukai es la plataforma donde gestionaremos y daremos seguimiento a tu trámite de crédito.",
    "Para continuar, crea tu acceso en este enlace:",
    registerUrl,
    "Desde ahí podrás subir documentos, completar información y revisar el avance de tu proceso.",
  ].join(" ");
  return `https://api.whatsapp.com/send/?phone=${phoneDigits}&text=${encodeURIComponent(message)}&type=phone_number&app_absent=0`;
}

/**
 * Modal Wizard para alta de solicitante / creación de cuenta de broker de créditos.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onCreated?: (result) => void
 */
export default function CreditBrokerOnboardingModal({
  open,
  onClose,
  onCreated,
}) {
  const institution = useSelector((state) => state.session?.institution);
  const globalUser = useSelector((state) => state.session?.globalUser);
  const [activeStep, setActiveStep] = useState(0);
  const [fieldTouched, setFieldTouched] = useState({});

  // Step 1 (manual)
  const [form, setForm] = useState({
    nombres: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    fechaNacimiento: "",
    rfc: "",
    email: "",
    telefono: "",
    producto: "hipotecario", // hipotecario | automotriz | personal | pyme
  });

  // Step 2 (docs)
  const [selectedDocType, setSelectedDocType] = useState("ine");
  const [docs, setDocs] = useState([]); // {id, type, file}
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [schemaDocTypes, setSchemaDocTypes] = useState([]);
  const [schemaForUploader, setSchemaForUploader] = useState(null);

  // Step 3 (extraction)
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [extracted, setExtracted] = useState(null); // objeto con kyc extraído
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [aiDocs, setAiDocs] = useState([]);

  // Step 4 (account)
  const [creating, setCreating] = useState(false);
  const [consultingBuro, setConsultingBuro] = useState(false);
  const [createdClientId, setCreatedClientId] = useState("");
  const [existingClients, setExistingClients] = useState([]);
  const [loadingExistingClients, setLoadingExistingClients] = useState(false);
  const [selectedExistingClientId, setSelectedExistingClientId] = useState("");
  const [existingClientsError, setExistingClientsError] = useState("");
  const [error, setError] = useState(null);
  const [buroResult, setBuroResult] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [diagnosticError, setDiagnosticError] = useState("");

  const docsByType = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const arr = map.get(d.type) || [];
      arr.push(d);
      map.set(d.type, arr);
    }
    return map;
  }, [docs]);

  const availableDocTypes = useMemo(() => {
    if (schemaDocTypes.length > 0) return schemaDocTypes;
    return Object.entries(docTypeLabels).map(([type, label]) => ({
      type,
      label,
      required: type === "ine" || type === "acta_nacimiento" || type === "curp",
      vc_tipo: "documento",
      fieldId: type,
    }));
  }, [schemaDocTypes]);

  const selectedDocMeta = useMemo(
    () => availableDocTypes.find((it) => it.type === selectedDocType) || null,
    [availableDocTypes, selectedDocType],
  );

  const selectedDocCount = useMemo(
    () =>
      selectedDocType ? (docsByType.get(selectedDocType) || []).length : 0,
    [docsByType, selectedDocType],
  );

  const selectedDocUploadedCount = useMemo(
    () =>
      selectedDocType
        ? (docsByType.get(selectedDocType) || []).filter((d) => !!d.documentId)
            .length
        : 0,
    [docsByType, selectedDocType],
  );

  const canGoNext = useMemo(() => {
    if (activeStep === 0) {
      if (form.email && !isValidEmail(form.email)) return false;
      if (form.telefono && !isValidPhoneMx(form.telefono)) return false;
      return true;
    }
    if (activeStep === 1) {
      if (availableDocTypes.length === 0) return docs.length > 0;
      const requiredDocs = availableDocTypes.filter((item) => !!item.required);
      if (requiredDocs.length === 0) return docs.length > 0;
      return requiredDocs.every(
        (item) => (docsByType.get(item.type) || []).length > 0,
      );
    }
    if (activeStep === 2) {
      if (!extracted) return false;
      if (extracted.curp && !isValidCurp(extracted.curp)) return false;
      if (extracted.email && !isValidEmail(extracted.email)) return false;
      if (extracted.telefono && !isValidPhoneMx(extracted.telefono)) return false;
      return true;
    }
    if (activeStep === 3) return true;
    return false;
  }, [activeStep, docsByType, extracted, form, docs.length, availableDocTypes]);

  const hasMinimumBuroData = useMemo(() => {
    const source = extracted || {};
    const nombres = `${source?.nombres || form.nombres || ""}`.trim();
    const apellidoPaterno =
      `${source?.apellidoPaterno || form.apellidoPaterno || ""}`.trim();
    const fechaNacimiento =
      `${source?.fechaNacimiento || form.fechaNacimiento || ""}`.trim();
    return !!(nombres && apellidoPaterno && fechaNacimiento);
  }, [extracted, form]);

  const inviteClientId = useMemo(
    () => createdClientId || selectedExistingClientId || "",
    [createdClientId, selectedExistingClientId],
  );

  const selectedExistingClient = useMemo(
    () =>
      existingClients.find((item) => item?.id === selectedExistingClientId) ||
      null,
    [existingClients, selectedExistingClientId],
  );

  const inviteWhatsappUrl = useMemo(
    () =>
      buildWhatsAppInviteUrl({
        phone: form.telefono || selectedExistingClient?.vc_telefono || "",
        clientId: inviteClientId,
        institutionId: institution?.id || "",
      }),
    [
      form.telefono,
      selectedExistingClient?.vc_telefono,
      inviteClientId,
      institution?.id,
    ],
  );

  function resetAll() {
    setActiveStep(0);
    setForm({
      nombres: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      fechaNacimiento: "",
      rfc: "",
      email: "",
      telefono: "",
      producto: "hipotecario",
    });
    setSelectedDocType("ine");
    setDocs([]);
    setExtracting(false);
    setExtractProgress(0);
    setExtracted(null);
    setAiDocs([]);
    setCreatedClientId("");
    setSelectedExistingClientId("");
    setBuroResult(null);
    setCreating(false);
    setConsultingBuro(false);
    setError(null);
  }

  function clientLabel(client) {
    const fullName = [client?.vc_nombre, client?.vc_paterno, client?.vc_materno]
      .filter(Boolean)
      .join(" ")
      .trim();
    return `${fullName || "Sin nombre"}${client?.vc_correo ? ` - ${client.vc_correo}` : ""}`;
  }

  function applyClientToForm(client) {
    if (!client) return;
    const birthDate =
      client?.fechaNacimiento ||
      client?.dt_nacimiento ||
      client?.usuario?.fechaNacimiento ||
      client?.usuario?.dt_nacimiento ||
      client?.user?.fechaNacimiento ||
      client?.user?.dt_nacimiento ||
      "";
    setCreatedClientId(client.id || "");
    setForm((prev) => ({
      ...prev,
      nombres: client?.vc_nombre || "",
      apellidoPaterno: client?.vc_paterno || "",
      apellidoMaterno: client?.vc_materno || "",
      fechaNacimiento: normalizeDateForInput(birthDate),
      rfc: (client?.vc_rfc || "").toUpperCase(),
      email: normalizeEmail(client?.vc_correo || ""),
      telefono: formatPhoneMx(client?.vc_telefono || ""),
    }));
  }

  useEffect(() => {
    if (!open || !institution?.id) return;
    let isMounted = true;
    setLoadingExistingClients(true);
    setExistingClientsError("");
    (async () => {
      try {
        const service = app.service("clients");
        const queries = [
          {
            scope: "all",
            id_institucion: institution.id,
            $limit: 300,
            $skip: 0,
            $sort: { updatedAt: -1 },
          },
          {
            scope: "mine_or_groups",
            id_institucion: institution.id,
            $limit: 300,
            $skip: 0,
            $sort: { updatedAt: -1 },
          },
          {
            scope: "mine",
            id_institucion: institution.id,
            $limit: 300,
            $skip: 0,
            $sort: { updatedAt: -1 },
          },
          {
            scope: "groups",
            id_institucion: institution.id,
            $limit: 300,
            $skip: 0,
            $sort: { updatedAt: -1 },
          },
          {
            id_institucion: institution.id,
            $limit: 300,
            $skip: 0,
            $sort: { updatedAt: -1 },
          },
          ...(globalUser?.id
            ? [
                {
                  id_institucion: institution.id,
                  id_usuario: globalUser.id,
                  $limit: 300,
                  $skip: 0,
                  $sort: { updatedAt: -1 },
                },
              ]
            : []),
        ];

        const merged = new Map();
        let lastError = "";

        for (const query of queries) {
          try {
            const response = await service.find({ query });
            const rows = Array.isArray(response?.data) ? response.data : [];
            rows.forEach((row) => {
              if (!row?.id) return;
              if (!merged.has(row.id)) merged.set(row.id, row);
            });
          } catch (e) {
            lastError = e?.message || lastError;
          }
        }

        if (!isMounted) return;
        const rows = Array.from(merged.values());
        setExistingClients(rows);
        if (!rows.length && lastError) {
          setExistingClientsError(lastError);
        }
      } catch (fetchError) {
        if (!isMounted) return;
        setExistingClients([]);
        setExistingClientsError(
          fetchError?.message || "No fue posible cargar clientes.",
        );
      } finally {
        if (!isMounted) return;
        setLoadingExistingClients(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [open, institution?.id, globalUser?.id]);

  useEffect(() => {
    if (!open || !institution?.id) return;
    let cancelled = false;
    const loadSchemaDocumentTypes = async () => {
      try {
        const productName =
          productLabelMap[form.producto] || productLabelMap.hipotecario;
        const productsResponse = await app.service("products").find({
          query: {
            id_institucion: institution.id,
            vc_nombre: productName,
            $limit: 1,
          },
        });
        const productRow = Array.isArray(productsResponse?.data)
          ? productsResponse.data[0]
          : null;
        if (!productRow?.id) {
          if (!cancelled) setSchemaDocTypes([]);
          return;
        }

        const schemaResponse = await app.service("product-form-schema").find({
          query: {
            id_institucion: institution.id,
            id_producto: productRow.id,
            $limit: 1,
          },
        });
        const payload = Array.isArray(schemaResponse?.data)
          ? schemaResponse.data[0]
          : null;
        if (!cancelled) setSchemaForUploader(payload);
        const processes = Array.isArray(payload?.processes)
          ? payload.processes
          : [];
        const collected = [];

        const walkProcesses = (nodes) => {
          (nodes || []).forEach((node) => {
            const fields = Array.isArray(node?.fields) ? node.fields : [];
            fields.forEach((field) => {
              if (!isDocumentFieldType(field?.vc_tipo)) return;
              collected.push({
                type: field?.vc_nombre || field?.id,
                label: field?.vc_label || field?.vc_nombre || "Documento",
                required: !!field?.bl_requerido,
                vc_tipo: field?.vc_tipo || "",
                fieldId: field?.id || field?.vc_nombre,
              });
            });
            if (
              Array.isArray(node?.sub_processes) &&
              node.sub_processes.length
            ) {
              walkProcesses(node.sub_processes);
            }
          });
        };

        walkProcesses(processes);
        const dedup = [];
        const seen = new Set();
        collected.forEach((item) => {
          const key = String(item.type || "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          dedup.push(item);
        });

        if (!cancelled) {
          setSchemaDocTypes(dedup);
          if (
            dedup.length > 0 &&
            !dedup.some((item) => item.type === selectedDocType)
          ) {
            setSelectedDocType(dedup[0].type);
          }
        }
      } catch (_error) {
        if (!cancelled) {
          setSchemaDocTypes([]);
          setSchemaForUploader(null);
        }
      }
    };

    loadSchemaDocumentTypes();
    return () => {
      cancelled = true;
    };
  }, [open, institution?.id, form.producto, selectedDocType]);

  function resolveClientIdFromPayload(payload) {
    if (!payload || typeof payload !== "object") return "";
    return (
      payload?.id_cliente ||
      payload?.idCliente ||
      payload?.cliente?.id ||
      payload?.client?.id ||
      payload?.lead?.id ||
      ""
    );
  }

  function handleClose() {
    resetAll();
    onClose?.();
  }

  const touchField = (key) =>
    setFieldTouched((prev) => ({ ...prev, [key]: true }));

  function openDeviceFileManager(type, options = {}) {
    const docType = type || selectedDocType;
    if (!docType) {
      setError("Selecciona primero un tipo de documento.");
      return;
    }
    setError(null);
    setSelectedDocType(docType);

    const useCamera = !!options.camera;
    const input = useCamera ? cameraInputRef.current : fileInputRef.current;
    if (!input) return;

    if (!useCamera) {
      input.setAttribute("accept", acceptFor(docType));
    }
    input.click();
  }

  async function getAccessToken() {
    let accessToken =
      localStorage.getItem("feathers-jwt") ||
      localStorage.getItem("accessToken") ||
      "";
    if (app?.authentication?.getAccessToken) {
      try {
        const resolvedToken = await app.authentication.getAccessToken();
        if (resolvedToken) accessToken = resolvedToken;
      } catch (tokenError) {
        // Fallback a localStorage
      }
    }
    return accessToken;
  }

  function addFiles(type, files) {
    if (!files || files.length === 0) return;
    const incoming = Array.from(files).map((f) => ({
      id: uid(),
      type,
      relationship: "titular",
      file: f,
      vc_ai_estatus: "pending",
      js_ai_resultado: null,
    }));
    setDocs((prev) => [...prev, ...incoming]);
  }

  function removeDoc(docId) {
    setDocs((prev) => prev.filter((d) => d.id !== docId));
  }

  function updateDocRelationship(docId, relationship) {
    setDocs((prev) =>
      prev.map((d) => (d.id === docId ? { ...d, relationship } : d)),
    );
  }

  async function ensureDocumentsUploaded() {
    if (!institution?.id) {
      throw new Error("Selecciona una institución antes de subir documentos.");
    }

    const pending = docs.filter((d) => !d.documentId);
    if (!pending.length) return docs;

    setUploadingDocs(true);
    const nextDocs = [...docs];

    try {
      for (const pendingDoc of pending) {
        const created = await uploadDocumentFile({
          file: pendingDoc.file,
          id_institucion: institution.id,
          id_owner: globalUser?.id || undefined,
          vc_nombre_original: pendingDoc.file?.name,
        });

        const index = nextDocs.findIndex((row) => row.id === pendingDoc.id);
        if (index >= 0) {
          nextDocs[index] = {
            ...nextDocs[index],
            documentId: created?.id || null,
            uploadedAt: new Date().toISOString(),
            vc_ai_estatus: created?.vc_ai_estatus || "pending",
            js_ai_resultado: created?.js_ai_resultado || null,
          };
        }
      }

      setDocs(nextDocs);
      return nextDocs;
    } finally {
      setUploadingDocs(false);
    }
  }

  async function fetchDocumentsByIds(ids) {
    if (!ids?.length) return [];
    const response = await app.service("documents").find({
      query: {
        id_institucion: institution?.id,
        id: { $in: ids },
        $limit: Math.max(100, ids.length + 5),
      },
    });
    return Array.isArray(response?.data) ? response.data : [];
  }

  function mergeExtractionFromProcessed(processedDocs) {
    const merged = {
      nombres: form.nombres || "",
      apellidoPaterno: form.apellidoPaterno || "",
      apellidoMaterno: form.apellidoMaterno || "",
      email: form.email || "",
      telefono: normalizePhoneForPayload(form.telefono || ""),
      producto: form.producto || "",
      observaciones: [],
      document_reports: [],
    };

    const incomes = [];
    for (const doc of processedDocs) {
      const result = doc?.js_ai_resultado || {};
      const fields = result?.fields_filled || {};
      const report = {
        document_id: doc?.id,
        document_type: result?.document_type || doc?.vc_tipo_archivo || "",
        status: doc?.vc_ai_estatus || "",
        fields_detected: fields,
        validations: result?.validations || {},
        ocr: result?.ocr || {},
        llm: result?.llm || {},
        activity_log: result?.activity_log || [],
      };
      merged.document_reports.push(report);

      Object.entries(fields || {}).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") return;
        if (key === "telefono" && merged.telefono) return;
        if (key === "ingresosMensuales") {
          const num = Number(value);
          if (Number.isFinite(num) && num > 0) incomes.push(num);
          return;
        }
        if (!merged[key]) merged[key] = value;
      });

      const llmWarnings = result?.llm?.warnings || [];
      const ocrWarnings = result?.ocr?.warnings || [];
      merged.observaciones.push(...llmWarnings, ...ocrWarnings);
    }

    if (incomes.length > 0) {
      const avg = incomes.reduce((acc, n) => acc + n, 0) / incomes.length;
      merged.ingresosMensuales = Math.round(avg * 100) / 100;
    }

    merged.observaciones = Array.from(
      new Set((merged.observaciones || []).filter(Boolean)),
    );
    return merged;
  }

  async function runExtraction() {
    setError(null);

    const ineCount = (docsByType.get("ine") || []).length;
    if (ineCount < 1) {
      setError("Sube al menos 1 INE para poder extraer identidad.");
      return;
    }

    setExtracting(true);
    setExtractProgress(5);

    let intervalId = null;

    try {
      const uploadedDocs = await ensureDocumentsUploaded();
      intervalId = window.setInterval(() => {
        setExtractProgress((p) =>
          p < 90 ? p + Math.round(Math.random() * 7) : p,
        );
      }, 350);

      const json = await extractKycWithAiBroker({
        producto: form.producto,
        seed_nombres: form.nombres,
        seed_apellidoPaterno: form.apellidoPaterno,
        seed_apellidoMaterno: form.apellidoMaterno,
        id_institucion: institution?.id || "",
        documents: uploadedDocs,
        document_ids: uploadedDocs
          .filter((d) => !!d.documentId)
          .map((d) => ({
            id: d.documentId,
            type: d.type,
            name: d.file?.name || "",
            relationship: d.relationship || "titular",
          })),
      });
      const kyc = (json && json.extracted) || {};

      window.clearInterval(intervalId);
      intervalId = null;
      setExtractProgress(100);

      setExtracted({
        ...kyc,
        nombres: kyc.nombres ?? form.nombres,
        apellidoPaterno: kyc.apellidoPaterno ?? form.apellidoPaterno,
        apellidoMaterno: kyc.apellidoMaterno ?? form.apellidoMaterno,
        fechaNacimiento: normalizeDateForInput(
          kyc.fechaNacimiento ?? form.fechaNacimiento,
        ),
        rfc: (kyc.rfc || form.rfc || "").toUpperCase(),
        email: normalizeEmail(kyc.email ?? form.email),
        telefono: formatPhoneMx(form.telefono || kyc.telefono || ""),
      });
    } catch (e) {
      setError(e?.message || "Error desconocido durante extracción en línea.");
    } finally {
      if (intervalId) window.clearInterval(intervalId);
      setExtracting(false);
    }
  }

  useEffect(() => {
    if (!open) return undefined;
    const uploadedIds = docs
      .filter((d) => !!d.documentId)
      .map((d) => d.documentId);
    if (!uploadedIds.length) return undefined;

    const service = socketApp.service("documents");
    const onDocumentUpdated = (payload) => {
      if (!payload?.id || !uploadedIds.includes(payload.id)) return;
      setAiDocs((prev) => {
        const next = [...prev];
        const index = next.findIndex((x) => x.id === payload.id);
        if (index >= 0) next[index] = { ...next[index], ...payload };
        else next.push(payload);
        return next;
      });
      setDocs((prev) =>
        prev.map((item) =>
          item.documentId === payload.id
            ? {
                ...item,
                vc_ai_estatus: payload.vc_ai_estatus || item.vc_ai_estatus,
                js_ai_resultado:
                  payload.js_ai_resultado || item.js_ai_resultado,
              }
            : item,
        ),
      );
    };

    service.on("patched", onDocumentUpdated);
    service.on("updated", onDocumentUpdated);

    return () => {
      service.off("patched", onDocumentUpdated);
      service.off("updated", onDocumentUpdated);
    };
  }, [open, docs]);

  useEffect(() => {
    if (activeStep !== 2) return;
    if (extracting) return;
    if (extracted) return;
    const processed = (aiDocs || []).filter(
      (d) => `${d?.vc_ai_estatus || ""}` === "done",
    );
    if (!processed.length) return;
    setExtracted(mergeExtractionFromProcessed(processed));
  }, [activeStep, extracting, extracted, aiDocs]);

  async function createAccount(options = {}) {
    const { closeAfter = true, notifyParent = true } = options;
    setError(null);

    const applicantData = extracted || {
      nombres: form.nombres || "",
      apellidoPaterno: form.apellidoPaterno || "",
      apellidoMaterno: form.apellidoMaterno || "",
      fechaNacimiento: form.fechaNacimiento || "",
      rfc: (form.rfc || "").toUpperCase(),
      email: form.email || "",
      telefono: normalizePhoneForPayload(form.telefono || ""),
    };

    if (!applicantData?.nombres || !applicantData?.apellidoPaterno) {
      setError(
        "Faltan datos mínimos del solicitante para crear el expediente.",
      );
      return null;
    }
    if (applicantData.curp && !isValidCurp(applicantData.curp)) {
      setError("CURP con formato inválido. Corrígela antes de continuar.");
      return null;
    }

    setCreating(true);
    try {
      const uploadedDocs = await ensureDocumentsUploaded();
      const normalizedApplicant = {
        ...applicantData,
        email: normalizeEmail(applicantData?.email || ""),
        telefono: normalizePhoneForPayload(applicantData?.telefono || ""),
      };
      const payload = {
        id_institucion: institution?.id || null,
        producto: form.producto,
        id_cliente: selectedExistingClientId || createdClientId || null,
        applicant: normalizedApplicant,
        documentos: uploadedDocs
          .filter((d) => !!d.documentId)
          .map((d) => ({
            id_documento: d.documentId,
            tipo: d.type,
            relationship: d.relationship || "titular",
            nombre: d.file?.name || "",
          })),
      };

      const accessToken = await getAccessToken();

      const res = await fetch(`${API_BASE_URL}/api/brokers/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(institution?.id ? { "x-institution-id": institution.id } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        const msg =
          errorBody?.message ||
          errorBody?.name ||
          `Error al crear cuenta (HTTP ${res.status})`;
        throw new Error(msg);
      }

      const json = await res.json();
      const clientId = resolveClientIdFromPayload(json);
      if (clientId) setCreatedClientId(clientId);

      if (notifyParent) onCreated?.(json);
      if (closeAfter) handleClose();
      return json;
    } catch (e) {
      setError(e?.message || "Error desconocido al crear la cuenta.");
      return null;
    } finally {
      setCreating(false);
    }
  }

  async function saveClientForWizard() {
    const created = await createAccount({
      closeAfter: false,
      notifyParent: false,
    });
    if (!created) return;
    const clientId = resolveClientIdFromPayload(created);
    if (clientId) {
      setCreatedClientId(clientId);
      setError(null);
    }
  }

  function shareClientByWhatsApp() {
    if (!inviteWhatsappUrl) {
      setError(
        "Primero guarda el cliente y captura un teléfono válido para compartir por WhatsApp.",
      );
      return;
    }
    window.open(inviteWhatsappUrl, "_blank", "noopener,noreferrer");
  }

  async function createAndConsultBuro() {
    setError(null);
    if (!hasMinimumBuroData) {
      setError(
        "Faltan datos mínimos para consultar buró: nombres, apellido paterno y fecha de nacimiento.",
      );
      return;
    }
    if (!institution?.id) {
      setError("Selecciona una institución antes de consultar buró.");
      return;
    }

    const created = await createAccount({
      closeAfter: false,
      notifyParent: false,
    });
    if (!created) return;

    const clientId =
      resolveClientIdFromPayload(created) ||
      createdClientId ||
      extracted?.id_cliente ||
      "";
    if (!clientId) {
      setError(
        "Se creó el cliente pero no se encontró id_cliente para consultar buró.",
      );
      return;
    }

    setConsultingBuro(true);
    try {
      const buroSource = extracted || form || {};
      const buro = await app.service("credit-reports").create({
        id_institucion: institution.id,
        id_cliente: clientId,
        vc_tipo_consulta: "rcc_ficoscore_pld",
        nombres: `${buroSource?.nombres || ""}`.trim(),
        apellidoPaterno: `${buroSource?.apellidoPaterno || ""}`.trim(),
        apellidoMaterno: `${buroSource?.apellidoMaterno || ""}`.trim(),
        fechaNacimiento: `${buroSource?.fechaNacimiento || ""}`.trim(),
        rfc: `${buroSource?.rfc || ""}`.trim().toUpperCase(),
        curp: `${buroSource?.curp || ""}`.trim().toUpperCase(),
      });
      setBuroResult(buro);
      onCreated?.({ ...created, credit_report: buro });
      handleClose();
    } catch (e) {
      setError(e?.message || "No fue posible consultar buró.");
    } finally {
      setConsultingBuro(false);
    }
  }

  async function runLiveBuroDiagnostics() {
    setDiagnosticError("");
    setDiagnosticResult(null);

    if (!institution?.id) {
      setDiagnosticError(
        "Selecciona una institución antes de ejecutar diagnóstico.",
      );
      setDiagnosticOpen(true);
      return;
    }

    setDiagnosticLoading(true);
    setDiagnosticOpen(true);

    try {
      const created = await createAccount({
        closeAfter: false,
        notifyParent: false,
      });
      if (!created) {
        throw new Error("No fue posible preparar el cliente para diagnóstico.");
      }

      const clientId =
        resolveClientIdFromPayload(created) ||
        createdClientId ||
        selectedExistingClientId ||
        "";
      if (!clientId) {
        throw new Error("No se encontró id_cliente para ejecutar diagnóstico.");
      }

      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/circulo/diagnostics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(institution?.id ? { "x-institution-id": institution.id } : {}),
        },
        body: JSON.stringify({
          id_institucion: institution.id,
          id_cliente: clientId,
          mode: "rcc_ficoscore_pld",
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          json?.message ||
          json?.error ||
          `Error diagnóstico (HTTP ${response.status})`;
        throw new Error(message);
      }

      setDiagnosticResult(json);
    } catch (diagError) {
      setDiagnosticError(
        diagError?.message || "No fue posible ejecutar diagnóstico en vivo.",
      );
    } finally {
      setDiagnosticLoading(false);
    }
  }

  function next() {
    setError(null);
    if (activeStep < steps.length - 1) setActiveStep((s) => s + 1);
  }

  function back() {
    setError(null);
    if (activeStep > 0) setActiveStep((s) => s - 1);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen="sm"
    >
      <DialogTitle sx={{ pr: 6 }}>
        Alta inicial — Gestor de Créditos
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stepper activeStep={activeStep}>
            {steps.map((s) => (
              <Step key={s}>
                <StepLabel>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error">{error}</Alert>}

          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Datos del solicitante (captura rápida)
              </Typography>

              <TextField
                label="Cliente existente (opcional)"
                value={selectedExistingClientId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedExistingClientId(nextId);
                  if (!nextId) {
                    setCreatedClientId("");
                    return;
                  }
                  const selectedClient =
                    existingClients.find((item) => item?.id === nextId) || null;
                  applyClientToForm(selectedClient);
                }}
                select
                fullWidth
                disabled={loadingExistingClients}
                sx={{ mb: 2 }}
                helperText={
                  loadingExistingClients
                    ? "Cargando clientes..."
                    : existingClientsError
                      ? existingClientsError
                      : "Si lo seleccionas, se precargan sus datos."
                }
              >
                <MenuItem value="">Nuevo cliente</MenuItem>
                {existingClients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {clientLabel(client)}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Nombres"
                  value={form.nombres}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, nombres: e.target.value }))
                  }
                  fullWidth
                />
                <TextField
                  label="Apellido paterno"
                  value={form.apellidoPaterno}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, apellidoPaterno: e.target.value }))
                  }
                  fullWidth
                />
                <TextField
                  label="Apellido materno"
                  value={form.apellidoMaterno}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, apellidoMaterno: e.target.value }))
                  }
                  fullWidth
                />
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 2 }}
              >
                <TextField
                  label="Fecha de nacimiento"
                  type="date"
                  value={form.fechaNacimiento}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, fechaNacimiento: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="RFC"
                  value={form.rfc}
                  onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        rfc: e.target.value.toUpperCase(),
                      }))
                  }
                  fullWidth
                />
                <TextField
                  label="Email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      email: normalizeEmail(e.target.value),
                    }))
                  }
                  onBlur={() => touchField("form.email")}
                  error={
                    !!(fieldTouched["form.email"] && form.email && !isValidEmail(form.email))
                  }
                  helperText={
                    fieldTouched["form.email"] && form.email && !isValidEmail(form.email)
                      ? "Email inválido"
                      : " "
                  }
                  fullWidth
                />
                <TextField
                  label="Teléfono"
                  value={form.telefono}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      telefono: formatPhoneMx(e.target.value),
                    }))
                  }
                  onBlur={() => touchField("form.telefono")}
                  error={
                    !!(
                      fieldTouched["form.telefono"] &&
                      form.telefono &&
                      !isValidPhoneMx(form.telefono)
                    )
                  }
                  helperText={
                    fieldTouched["form.telefono"] &&
                    form.telefono &&
                    !isValidPhoneMx(form.telefono)
                      ? "Teléfono inválido. Debe tener 10 dígitos."
                      : " "
                  }
                  inputProps={{ inputMode: "numeric", maxLength: 17 }}
                  fullWidth
                />
                <TextField
                  label="Producto"
                  value={form.producto}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, producto: e.target.value }))
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="hipotecario">Crédito hipotecario</MenuItem>
                  <MenuItem value="automotriz">Crédito automotriz</MenuItem>
                  <MenuItem value="personal">Crédito personal</MenuItem>
                  <MenuItem value="pyme">Crédito PyME</MenuItem>
                </TextField>
              </Stack>

              <Alert severity="info" sx={{ mt: 2 }}>
                Estos datos son para iniciar. Luego subes documentos y el
                sistema extrae la información para completar el alta.
              </Alert>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ mt: 2 }}
              >
                <Button
                  variant="contained"
                  onClick={saveClientForWizard}
                  disabled={creating || consultingBuro}
                >
                  {creating ? "Guardando cliente..." : "Guardar cliente"}
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={shareClientByWhatsApp}
                  disabled={!inviteWhatsappUrl || creating || consultingBuro}
                >
                  Compartir por WhatsApp
                </Button>
              </Stack>

              {inviteClientId && (
                <Typography variant="caption" color="text.secondary">
                  Cliente listo para compartir: {inviteClientId}
                </Typography>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Subida de documentos (KYC)
              </Typography>

              <Box sx={{ mb: 2 }}>
                <SubirDocumentosInteligente
                  id_cliente={
                    createdClientId || selectedExistingClientId || undefined
                  }
                  id_institucion={institution?.id}
                  id_usuario={globalUser?.id}
                  id_product_assignment={null}
                  schema={schemaForUploader}
                  buttonText="Subir Documentos"
                  onSaved={({ item, document, docType, extractedData }) => {
                    setDocs((prev) => [
                      ...prev,
                      {
                        id: uid(),
                        type: docType || selectedDocType || "otros",
                        relationship: "titular",
                        file: item?.file,
                        documentId: document?.id || null,
                        uploadedAt: new Date().toISOString(),
                        vc_ai_estatus: document?.vc_ai_estatus || "pending",
                        js_ai_resultado: document?.js_ai_resultado || null,
                      },
                    ]);
                    setExtracted((prev) => ({
                      ...(prev || {}),
                      ...(extractedData || {}),
                      email: normalizeEmail(
                        extractedData?.email || prev?.email || form.email || "",
                      ),
                      telefono: formatPhoneMx(
                        extractedData?.telefono ||
                          prev?.telefono ||
                          form.telefono ||
                          "",
                      ),
                    }));
                  }}
                  onError={(smartUploadError) =>
                    setError(
                      smartUploadError?.message ||
                        "No fue posible procesar documentos en carga inteligente.",
                    )
                  }
                />
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptFor(selectedDocType)}
                style={{ display: "none" }}
                onChange={(e) => {
                  addFiles(selectedDocType, e.target.files);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={(e) => {
                  addFiles(selectedDocType, e.target.files);
                  if (cameraInputRef.current) cameraInputRef.current.value = "";
                }}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {availableDocTypes.map((item, index) => {
                  const typeCount = (docsByType.get(item.type) || []).length;
                  const uploadedCount = (
                    docsByType.get(item.type) || []
                  ).filter((d) => !!d.documentId).length;
                  const completed = typeCount > 0;
                  const active = selectedDocType === item.type;
                  return (
                    <ButtonDocsInput
                      key={index}
                      active={active}
                      item={item}
                      completed={completed}
                      totalCount={typeCount}
                      uploadedCount={uploadedCount}
                      accept={acceptFor(item.type)}
                      onPickFiles={(files) => addFiles(item.type, files)}
                      onSelectType={() => setSelectedDocType(item.type)}
                    />
                  );
                })}
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mt: 1.5 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  Tipo seleccionado:{" "}
                  <b>{selectedDocMeta?.label || "Selecciona un tipo"}</b>
                </Typography>
              </Stack>
              {!!selectedDocType && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {selectedDocUploadedCount > 0
                    ? `Ya hay ${selectedDocUploadedCount} documento(s) subido(s) en este tipo.`
                    : selectedDocCount > 0
                      ? `Ya hay ${selectedDocCount} documento(s) agregado(s) en este tipo.`
                      : "Selecciona o toma archivo para este tipo de documento."}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                {availableDocTypes.map((item) => {
                  const type = item.type;
                  const list = docsByType.get(type) || [];
                  if (list.length === 0) return null;

                  return (
                    <Box
                      key={type}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {item.label} ({list.length})
                      </Typography>

                      <Stack spacing={1}>
                        {list.map((d) => (
                          <Stack
                            key={d.id}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="body2">
                                {d.file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {humanFileSize(d.file.size)} —{" "}
                                {d.file.type || "tipo desconocido"}
                              </Typography>
                              <Box mt={1}>
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 180 }}
                                >
                                  <InputLabel>Relacion</InputLabel>
                                  <Select
                                    label="Relacion"
                                    value={d.relationship || "titular"}
                                    onChange={(e) =>
                                      updateDocRelationship(
                                        d.id,
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <MenuItem value="titular">Titular</MenuItem>
                                    <MenuItem value="coacreditado">
                                      Coacreditado
                                    </MenuItem>
                                    <MenuItem value="conyuge">Conyuge</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                              {!!d.documentId && (
                                <Typography
                                  variant="caption"
                                  color="success.main"
                                  display="block"
                                >
                                  Subido
                                </Typography>
                              )}
                              {!!d.documentId && !!d.vc_ai_estatus && (
                                <Typography
                                  variant="caption"
                                  color={
                                    d.vc_ai_estatus === "done"
                                      ? "success.main"
                                      : d.vc_ai_estatus === "error"
                                        ? "error.main"
                                        : "warning.main"
                                  }
                                  display="block"
                                >
                                  AI: {d.vc_ai_estatus}
                                </Typography>
                              )}
                            </Box>

                            <IconButton
                              onClick={() => removeDoc(d.id)}
                              aria-label="Eliminar"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}

                {docs.length === 0 && (
                  <Alert severity="warning">
                    Aún no has subido documentos. Mínimo requerido: <b>INE</b> y{" "}
                    <b>Acta o CURP</b>.
                  </Alert>
                )}
              </Stack>

              <Alert severity="info" sx={{ mt: 2 }}>
                Tip: estados de cuenta en PDF ayudan a detectar banco, CLABE y
                estimar ingresos.
              </Alert>
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Extracción y validación
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <Button
                  variant="contained"
                  onClick={runExtraction}
                  disabled={extracting || docs.length === 0}
                >
                  Procesar documentos (cola AI)
                </Button>

                <Box sx={{ flex: 1 }}>
                  {extracting && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        Procesando OCR / lectura de PDFs…
                      </Typography>
                      <LinearProgress
                        value={extractProgress}
                        variant="determinate"
                      />
                    </>
                  )}
                </Box>

                {(extracting || creating || uploadingDocs) && (
                  <CircularProgress size={22} />
                )}
              </Stack>

              {aiDocs.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, flexWrap: "wrap" }}
                >
                  <Chip label={`Docs AI: ${aiDocs.length}`} />
                  <Chip
                    color="success"
                    label={`Done: ${aiDocs.filter((d) => `${d?.vc_ai_estatus || ""}` === "done").length}`}
                  />
                  <Chip
                    color="warning"
                    label={`Processing: ${aiDocs.filter((d) => ["pending", "processing", ""].includes(`${d?.vc_ai_estatus || ""}`)).length}`}
                  />
                  <Chip
                    color="error"
                    label={`Error: ${aiDocs.filter((d) => `${d?.vc_ai_estatus || ""}` === "error").length}`}
                  />
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />

              {!extracted ? (
                <Alert severity="warning">
                  Ejecuta la extracción para prellenar datos. Después podrás
                  corregir cualquier campo antes de crear la cuenta.
                </Alert>
              ) : (
                <Box>
                  {extracted?.document_validation?.valid === false && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      Se detectaron diferencias de titularidad en algunos
                      documentos. Puedes continuar y corregir manualmente.
                    </Alert>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={`Confianza: ${Math.round((extracted.confianza ?? 0.7) * 100)}%`}
                      color={
                        (extracted.confianza ?? 0.7) >= 0.75
                          ? "success"
                          : "warning"
                      }
                    />
                    {extracted?.validaciones?.rfc_formato_valido === false && (
                      <Chip color="error" label="RFC inválido (formato)" />
                    )}
                    {extracted?.validaciones?.rfc_digito_verificador_valido ===
                      false && (
                      <Chip
                        color="error"
                        label="RFC inválido (dígito verificador)"
                      />
                    )}
                    {extracted?.validaciones?.rfc_consistente_nombre_fecha ===
                      false && (
                      <Chip
                        color="warning"
                        label="RFC no consistente con nombre/fecha"
                      />
                    )}
                    {(extracted.observaciones || [])
                      .slice(0, 6)
                      .map((o, idx) => (
                        <Chip key={idx} label={o} />
                      ))}
                  </Stack>

                  {(extracted.document_reports || []).length > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Documentos validados:{" "}
                      {(extracted.document_reports || []).length}. Se aplicó
                      extracción incremental por documento.
                    </Alert>
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Confirma / corrige los datos detectados
                  </Typography>

                  <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Nombres"
                        value={extracted.nombres || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            nombres: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Apellido paterno"
                        value={extracted.apellidoPaterno || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            apellidoPaterno: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Apellido materno"
                        value={extracted.apellidoMaterno || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            apellidoMaterno: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Fecha de nacimiento (YYYY-MM-DD)"
                        value={extracted.fechaNacimiento || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            fechaNacimiento: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="CURP"
                        value={(extracted.curp || "").toUpperCase()}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            curp: e.target.value.toUpperCase(),
                          }))
                        }
                        error={!isValidCurp(extracted.curp)}
                        helperText={
                          !isValidCurp(extracted.curp)
                            ? "Formato CURP inválido"
                            : " "
                        }
                        fullWidth
                      />
                      <TextField
                        label="RFC"
                        value={(extracted.rfc || "").toUpperCase()}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            rfc: e.target.value.toUpperCase(),
                          }))
                        }
                        fullWidth
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Email"
                        value={extracted.email || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            email: normalizeEmail(e.target.value),
                          }))
                        }
                        onBlur={() => touchField("extracted.email")}
                        error={
                          !!(
                            fieldTouched["extracted.email"] &&
                            extracted.email &&
                            !isValidEmail(extracted.email)
                          )
                        }
                        helperText={
                          fieldTouched["extracted.email"] &&
                          extracted.email &&
                          !isValidEmail(extracted.email)
                            ? "Email inválido"
                            : " "
                        }
                        fullWidth
                      />
                      <TextField
                        label="Teléfono"
                        value={extracted.telefono || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            telefono: formatPhoneMx(e.target.value),
                          }))
                        }
                        onBlur={() => touchField("extracted.telefono")}
                        error={
                          !!(
                            fieldTouched["extracted.telefono"] &&
                            extracted.telefono &&
                            !isValidPhoneMx(extracted.telefono)
                          )
                        }
                        helperText={
                          fieldTouched["extracted.telefono"] &&
                          extracted.telefono &&
                          !isValidPhoneMx(extracted.telefono)
                            ? "Teléfono inválido. Debe tener 10 dígitos."
                            : " "
                        }
                        inputProps={{ inputMode: "numeric", maxLength: 17 }}
                        fullWidth
                      />
                    </Stack>

                    <Divider />

                    <Typography variant="subtitle2">Domicilio</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Calle"
                        value={extracted.calle || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            calle: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="No. ext"
                        value={extracted.numeroExt || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            numeroExt: e.target.value,
                          }))
                        }
                        sx={{ maxWidth: 160 }}
                      />
                      <TextField
                        label="No. int"
                        value={extracted.numeroInt || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            numeroInt: e.target.value,
                          }))
                        }
                        sx={{ maxWidth: 160 }}
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Colonia"
                        value={extracted.colonia || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            colonia: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Municipio"
                        value={extracted.municipio || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            municipio: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Estado"
                        value={extracted.estado || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            estado: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="CP"
                        value={extracted.cp || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            cp: e.target.value,
                          }))
                        }
                        sx={{ maxWidth: 160 }}
                      />
                    </Stack>

                    <Divider />

                    <Typography variant="subtitle2">
                      Bancario (si aplica)
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Banco"
                        value={extracted.banco || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            banco: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="CLABE"
                        value={extracted.clabe || ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            clabe: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Ingreso mensual (estimado)"
                        type="number"
                        value={extracted.ingresosMensuales ?? ""}
                        onChange={(e) =>
                          setExtracted((p) => ({
                            ...(p || {}),
                            ingresosMensuales: e.target.value
                              ? Number(e.target.value)
                              : undefined,
                          }))
                        }
                        fullWidth
                      />
                    </Stack>
                  </Stack>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    Lo confirmado aquí se usa para crear el expediente y la
                    cuenta del broker (KYC).
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {activeStep === 3 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Crear cuenta y finalizar
              </Typography>

              {!extracted ? (
                <Alert severity="error">
                  Falta extracción/validación. Regresa al paso anterior.
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Listo para crear la cuenta del broker con la información
                    confirmada.
                  </Alert>

                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <b>Solicitante:</b>{" "}
                      {`${extracted.nombres || ""} ${extracted.apellidoPaterno || ""} ${extracted.apellidoMaterno || ""}`.trim()}
                    </Typography>
                    <Typography variant="body2">
                      <b>CURP:</b> {extracted.curp || "—"} &nbsp; <b>RFC:</b>{" "}
                      {extracted.rfc || "—"}
                    </Typography>
                    <Typography variant="body2">
                      <b>Email:</b> {extracted.email || "—"} &nbsp; <b>Tel:</b>{" "}
                      {extracted.telefono || "—"}
                    </Typography>
                    <Typography variant="body2">
                      <b>Docs subidos:</b> {docs.length}
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="contained"
                    onClick={createAccount}
                    disabled={creating}
                  >
                    {creating ? "Creando cuenta…" : "Crear cuenta"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={createAndConsultBuro}
                    disabled={creating || consultingBuro || !hasMinimumBuroData}
                  >
                    {consultingBuro
                      ? "Consultando buró…"
                      : "Crear y consultar buró"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={runLiveBuroDiagnostics}
                    disabled={creating || consultingBuro || diagnosticLoading}
                  >
                    {diagnosticLoading
                      ? "Diagnosticando…"
                      : "Diagnóstico buró (sandbox)"}
                  </Button>

                  {!hasMinimumBuroData && (
                    <Alert severity="warning">
                      Para buró se requiere mínimo: nombres, apellido paterno y
                      fecha de nacimiento.
                    </Alert>
                  )}

                  {buroResult && (
                    <Alert severity="info">
                      Consulta buró realizada. Estatus:{" "}
                      {buroResult?.vc_estatus || buroResult?.status || "—"}.
                    </Alert>
                  )}

                  {(creating || consultingBuro) && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress />
                      <Typography variant="caption" color="text.secondary">
                        {consultingBuro
                          ? "Ejecutando consulta de buró…"
                          : "Registrando expediente, usuario y perfil del broker…"}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          onClick={back}
          disabled={
            activeStep === 0 || extracting || creating || consultingBuro
          }
        >
          Atrás
        </Button>

        {activeStep === 0 && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={createAndConsultBuro}
            disabled={creating || consultingBuro || !hasMinimumBuroData}
          >
            {consultingBuro ? "Consultando buró…" : "Crear y consultar buró"}
          </Button>
        )}

        {activeStep === 3 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={createAndConsultBuro}
            disabled={creating || consultingBuro || !hasMinimumBuroData}
          >
            {consultingBuro ? "Consultando buró…" : "Crear y consultar buró"}
          </Button>
        )}

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={next}
            disabled={!canGoNext || extracting || creating || consultingBuro}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
            disabled={extracting || creating || consultingBuro}
          >
            Reiniciar
          </Button>
        )}
      </DialogActions>

      <Dialog
        open={diagnosticOpen}
        onClose={() => setDiagnosticOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Diagnóstico en vivo de buró</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {diagnosticLoading && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  Ejecutando pruebas de configuración y conectividad...
                </Typography>
              </Box>
            )}

            {!diagnosticLoading && diagnosticError && (
              <Alert severity="error">{diagnosticError}</Alert>
            )}

            {!diagnosticLoading && !diagnosticError && diagnosticResult && (
              <>
                <Alert severity="success">Diagnóstico completado.</Alert>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Respuesta (JSON)
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      maxHeight: 420,
                      overflow: "auto",
                      bgcolor: "rgba(0,0,0,0.04)",
                      fontSize: 12,
                    }}
                  >
                    {JSON.stringify(diagnosticResult, null, 2)}
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagnosticOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
